// @flow
import type { Element } from 'react';

import React from 'react';
import { Col, Form, FormText, FormGroup, Row } from 'reactstrap';
import {
  ALLOW_FIRE_WATCH_TO_BE_SUFFICIENT,
  ALLOW_TEMPORARY_REPAIRS,
  ALLOW_TEMPORARY_SHORING,
  BUILDING_REPLACEMENT_TIME,
  CASH_ON_HAND,
  DAMAGE_CORRELATION_MODEL,
  DOOR_RACKING_AFFECTS_EGRESS,
  EQUIPMENT_PREQUALIFIED,
  INCLUDE_FLOODING_IMPACT,
  INCLUDE_IMPEDANCE_CONTRACTOR,
  INCLUDE_IMPEDANCE_ENGINEERING,
  INCLUDE_IMPEDANCE_FINANCING,
  INCLUDE_IMPEDANCE_INSPECTION,
  INCLUDE_IMPEDANCE_LONG_LEAD_TIME,
  INCLUDE_IMPEDANCE_PERMITTING,
  INCLUDE_SURGE_DEMAND,
  IS_DENSE_URBAN_AREA,
  MAX_WALKABLE_STORY,
  MINIMUM_NUMBER_OF_EXITS_REQUIRED,
  MITIGATION_CONTRACTOR,
  MITIGATION_ENGINEER,
  MITIGATION_FUNDING_SOURCE,
  MITIGATION_INSPECTOR,
  NEED_COOLING_FOR_FUNCTION,
  NEED_COOLING_FOR_REOCCUPANCY,
  NEED_ELECTRICAL_POWER_FOR_FUNCTION,
  NEED_ELECTRICAL_POWER_FOR_REOCCUPANCY,
  NEED_ELEVATOR_FOR_FUNCTION,
  NEED_EXHAUST_FOR_FUNCTION,
  NEED_EXHAUST_FOR_REOCCUPANCY,
  NEED_HEATING_FOR_FUNCTION,
  NEED_HEATING_FOR_REOCCUPANCY,
  NEED_POTABLE_WATER_FOR_FUNCTION,
  NEED_POTABLE_WATER_FOR_REOCCUPANCY,
  NEED_SANITATION_PLUMBING_FOR_FUNCTION,
  NEED_SANITATION_PLUMBING_FOR_REOCCUPANCY,
  NEED_VENTILATION_FOR_FUNCTION,
  NEED_VENTILATION_FOR_REOCCUPANCY,
  PERCENT_OF_EXITS_REQUIRED,
  PREQUALIFIED_ACC_GROUND,
  PREQUALIFIED_ACC_ROOF,
  PREQUALIFIED_DESIGN_CODE_YEAR,
  REPAIR_TIME_METHOD,
  USE_SMART_EQUIPMENT_COMPONENTS,
  USE_SMART_PIPING_COMPONENTS,
  USE_UPDATED_ELEVATOR_COMPONENTS,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import BackButton from '#components/pages/Models/Model/sections/support/buttons/BackButton.jsx';
import NextButton from '#components/pages/Models/Model/sections/support/buttons/NextButton.jsx';
import GridField from '#components/pages/Models/Model/sections/support/forms/GridField/index.jsx';
import YesNoSelectMenu from '#components/pages/Models/Model/sections/support/forms/YesNoSelectMenu/index.jsx';
import Tip from '#components/support/tooltips/Tip/index.jsx';
import FundingSourceSelectMenu from '#components/pages/Models/Model/sections/support/forms/FundingSourceSelectMenu/index.jsx';
import RepairTimeMethodSelectMenu from './RepairTimeMethodSelectMenu/index.jsx';
import ContractorRelationshipSelectMenu from './ContractorRelationshipSelectMenu/index.jsx';
import DamageCorrelationModelSelectMenu from './DamageCorrelationModelSelectMenu/index.jsx';
import EquipmentPrequalifiedDesignCodeYearSelectMenu from './EquipmentPrequalifiedDesignCodeYearSelectMenu/index.jsx';

type Props = {|
  form: string,
  handleSubmit: Function,
  heading: string,
  onBack: Function,
  onNext: Function,
  showDefaultATC138Fields: boolean,
  showIsDenseUrbanArea: boolean,
  showMaxWalkableStory: boolean,
  showEquipmentPrequalified: boolean,
  showPrequalifiedDesignCodeYear: boolean,
  showPrequalifiedDetails: boolean,
  showIncludeImpedanceLongLeadTimeWarning: boolean,
|};

const BuildingStabilitySection = (props: Props): Element<'div'> => {
  const {
    handleSubmit,
    heading,
    form,
    onBack,
    onNext,
    showDefaultATC138Fields,
    showIsDenseUrbanArea,
    showMaxWalkableStory,
    showEquipmentPrequalified,
    showPrequalifiedDesignCodeYear,
    showPrequalifiedDetails,
    showIncludeImpedanceLongLeadTimeWarning,
  } = props;

  return (
    <div>
      <h2>{heading}</h2>
      <Form noValidate onSubmit={handleSubmit}>
        <GridField
          name={REPAIR_TIME_METHOD}
          form={form}
          options={{
            columnSize: 6,
            labelText: 'Recovery Time Method',
            ComponentType: RepairTimeMethodSelectMenu,
            includeToolTip: true,
          }}
        />
        <FormGroup tag="fieldset">
          <legend>General</legend>
          <GridField
            name={BUILDING_REPLACEMENT_TIME}
            form={form}
            options={{
              columnSize: 4,
              labelText: 'Building Replacement Time',
              ComponentType: 'text',
              includeToolTip: true,
              inputGroup: {
                addonType: 'append',
                addonText: 'days',
              },
            }}
          />
          {showDefaultATC138Fields && (
            <GridField
              name={DAMAGE_CORRELATION_MODEL}
              form={form}
              options={{
                columnSize: 6,
                labelText: 'Statistical Damage Correlation Model',
                ComponentType: DamageCorrelationModelSelectMenu,
                includeToolTip: true,
              }}
            />
          )}
        </FormGroup>
        <FormGroup tag="fieldset">
          <legend>
            Factors Delaying the Start of Repairs <Tip tipId="factorsDelayingTheStartOfRepairs" />
          </legend>
          <GridField
            name={INCLUDE_IMPEDANCE_INSPECTION}
            form={form}
            options={{
              columnSize: 6,
              labelText: 'Inspection',
              ComponentType: 'checkbox',
            }}
          />
          <GridField
            name={INCLUDE_IMPEDANCE_FINANCING}
            form={form}
            options={{
              columnSize: 6,
              labelText: 'Financing',
              ComponentType: 'checkbox',
            }}
          />
          <GridField
            name={INCLUDE_IMPEDANCE_PERMITTING}
            form={form}
            options={{
              columnSize: 6,
              labelText: 'Permitting',
              ComponentType: 'checkbox',
            }}
          />
          <GridField
            name={INCLUDE_IMPEDANCE_ENGINEERING}
            form={form}
            options={{
              columnSize: 6,
              labelText: 'Engineering Mobilization and Design',
              ComponentType: 'checkbox',
            }}
          />
          <GridField
            name={INCLUDE_IMPEDANCE_CONTRACTOR}
            form={form}
            options={{
              columnSize: 6,
              labelText: 'Contractor Mobilization',
              ComponentType: 'checkbox',
            }}
          />
          {showDefaultATC138Fields && (
            <>
              <GridField
                name={INCLUDE_IMPEDANCE_LONG_LEAD_TIME}
                form={form}
                options={{
                  columnSize: 6,
                  labelText: 'Long Lead Time',
                  ComponentType: 'checkbox',
                  includeToolTip: true,
                }}
              />
              {showIncludeImpedanceLongLeadTimeWarning && (
                <FormText
                  className="mt-0 pt-0 pl-4"
                  id="includeImpedanceLongLeadTimeWarning"
                  color="muted"
                >
                  * The official ATC-138 recommendation is to include Long Lead Time
                </FormText>
              )}
            </>
          )}
        </FormGroup>

        <FormGroup tag="fieldset">
          <legend>
            Mitigation Factors <Tip tipId="mitigationFactors" />
          </legend>
          <GridField
            name={MITIGATION_INSPECTOR}
            form={form}
            options={{
              columnSize: 6,
              labelText: 'Inspector on Retainer',
              ComponentType: 'checkbox',
              includeToolTip: true,
            }}
          />
          <GridField
            name={MITIGATION_ENGINEER}
            form={form}
            options={{
              columnSize: 6,
              labelText: 'Engineer on Retainer',
              ComponentType: 'checkbox',
              includeToolTip: true,
            }}
          />
          <GridField
            name={MITIGATION_CONTRACTOR}
            form={form}
            options={{
              columnSize: 6,
              labelText: 'Contractor Relationship',
              ComponentType: ContractorRelationshipSelectMenu,
              includeToolTip: true,
            }}
          />
          <GridField
            name={MITIGATION_FUNDING_SOURCE}
            form={form}
            options={{
              columnSize: 6,
              labelText: 'Funding Source',
              ComponentType: FundingSourceSelectMenu,
              includeToolTip: true,
            }}
          />
          {showDefaultATC138Fields && (
            <GridField
              name={CASH_ON_HAND}
              form={form}
              options={{
                columnSize: 4,
                labelText: 'Cash on Hand',
                ComponentType: 'text',
                inputGroup: {
                  addonType: 'append',
                  addonText: '%',
                },
                includeToolTip: true,
              }}
            />
          )}
        </FormGroup>
        {showDefaultATC138Fields && (
          <FormGroup tag="fieldset" id="atc138FunctionalRecoveryOptions">
            <legend>ATC-138 Functional Recovery (Beta) Options</legend>
            <FormGroup tag="fieldset">
              <legend>HVAC</legend>
              <FormGroup>
                <Row>
                  <Col lg="4" className="font-weight-bold">
                    Ventilation
                  </Col>
                </Row>
                <GridField
                  name={NEED_VENTILATION_FOR_REOCCUPANCY}
                  form={form}
                  options={{
                    columnSize: 4,
                    columnClass: 'ml-3',
                    labelText: 'Required for Occupancy?',
                    componentProps: {
                      inputClassName: 'need-ventilation-for-reoccupancy-select',
                    },
                    ComponentType: YesNoSelectMenu,
                    includeToolTip: false,
                  }}
                />
                <GridField
                  name={NEED_VENTILATION_FOR_FUNCTION}
                  form={form}
                  options={{
                    columnSize: 4,
                    columnClass: 'ml-3',
                    labelText: 'Required for Function?',
                    componentProps: {
                      inputClassName: 'need-ventilation-for-function-select',
                    },
                    ComponentType: YesNoSelectMenu,
                    includeToolTip: false,
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Row>
                  <Col lg="4" className="font-weight-bold">
                    Cooling
                  </Col>
                </Row>
                <GridField
                  name={NEED_COOLING_FOR_REOCCUPANCY}
                  form={form}
                  options={{
                    columnSize: 4,
                    columnClass: 'ml-3',
                    labelText: 'Required for Occupancy?',
                    componentProps: {
                      inputClassName: 'need-cooling-for-reoccupancy-select',
                    },
                    ComponentType: YesNoSelectMenu,
                    includeToolTip: false,
                  }}
                />
                <GridField
                  name={NEED_COOLING_FOR_FUNCTION}
                  form={form}
                  options={{
                    columnSize: 4,
                    columnClass: 'ml-3',
                    labelText: 'Required for Function?',
                    componentProps: {
                      inputClassName: 'need-cooling-for-function-select',
                    },
                    ComponentType: YesNoSelectMenu,
                    includeToolTip: false,
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Row>
                  <Col lg="4" className="font-weight-bold">
                    Heating
                  </Col>
                </Row>
                <GridField
                  name={NEED_HEATING_FOR_REOCCUPANCY}
                  form={form}
                  options={{
                    columnSize: 4,
                    columnClass: 'ml-3',
                    labelText: 'Required for Occupancy?',
                    componentProps: {
                      inputClassName: 'need-heating-for-reoccupancy-select',
                    },
                    ComponentType: YesNoSelectMenu,
                    includeToolTip: false,
                  }}
                />
                <GridField
                  name={NEED_HEATING_FOR_FUNCTION}
                  form={form}
                  options={{
                    columnSize: 4,
                    columnClass: 'ml-3',
                    labelText: 'Required for Function?',
                    componentProps: {
                      inputClassName: 'need-heating-for-function-select',
                    },
                    ComponentType: YesNoSelectMenu,
                    includeToolTip: false,
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Row>
                  <Col lg="4" className="font-weight-bold">
                    Exhaust
                  </Col>
                </Row>
                <GridField
                  name={NEED_EXHAUST_FOR_REOCCUPANCY}
                  form={form}
                  options={{
                    columnSize: 4,
                    columnClass: 'ml-3',
                    labelText: 'Required for Occupancy?',
                    componentProps: {
                      inputClassName: 'need-exhaust-for-reoccupancy-select',
                    },
                    ComponentType: YesNoSelectMenu,
                    includeToolTip: false,
                  }}
                />
                <GridField
                  name={NEED_EXHAUST_FOR_FUNCTION}
                  form={form}
                  options={{
                    columnSize: 4,
                    columnClass: 'ml-3',
                    labelText: 'Required for Function?',
                    componentProps: {
                      inputClassName: 'need-exhaust-for-function-select',
                    },
                    ComponentType: YesNoSelectMenu,
                    includeToolTip: false,
                  }}
                />
              </FormGroup>
            </FormGroup>
            <FormGroup tag="fieldset">
              <legend>Plumbing</legend>
              <FormGroup>
                <Row>
                  <Col lg="4" className="font-weight-bold">
                    Potable Water
                  </Col>
                </Row>
                <GridField
                  name={NEED_POTABLE_WATER_FOR_REOCCUPANCY}
                  form={form}
                  options={{
                    columnSize: 4,
                    columnClass: 'ml-3',
                    labelText: 'Required for Occupancy?',
                    componentProps: {
                      inputClassName: 'need-potable-water-for-reoccupancy-select',
                    },
                    ComponentType: YesNoSelectMenu,
                    includeToolTip: false,
                  }}
                />
                <GridField
                  name={NEED_POTABLE_WATER_FOR_FUNCTION}
                  form={form}
                  options={{
                    columnSize: 4,
                    columnClass: 'ml-3',
                    labelText: 'Required for Function?',
                    componentProps: {
                      inputClassName: 'need-potable-water-for-function-select',
                    },
                    ComponentType: YesNoSelectMenu,
                    includeToolTip: false,
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Row>
                  <Col lg="4" className="font-weight-bold">
                    Sanitation Plumbing
                  </Col>
                </Row>
                <GridField
                  name={NEED_SANITATION_PLUMBING_FOR_REOCCUPANCY}
                  form={form}
                  options={{
                    columnSize: 4,
                    columnClass: 'ml-3',
                    labelText: 'Required for Occupancy?',
                    componentProps: {
                      inputClassName: 'need-sanitation-plumbing-for-reoccupancy-select',
                    },
                    ComponentType: YesNoSelectMenu,
                    includeToolTip: false,
                  }}
                />
                <GridField
                  name={NEED_SANITATION_PLUMBING_FOR_FUNCTION}
                  form={form}
                  options={{
                    columnSize: 4,
                    columnClass: 'ml-3',
                    labelText: 'Required for Function?',
                    componentProps: {
                      inputClassName: 'need-sanitation-plumbing-for-function-select',
                    },
                    ComponentType: YesNoSelectMenu,
                    includeToolTip: false,
                  }}
                />
              </FormGroup>
            </FormGroup>
            <FormGroup tag="fieldset">
              <legend>Electrical</legend>
              <FormGroup>
                <Row>
                  <Col lg="4" className="font-weight-bold">
                    Electrical Power
                  </Col>
                </Row>
                <GridField
                  name={NEED_ELECTRICAL_POWER_FOR_REOCCUPANCY}
                  form={form}
                  options={{
                    columnSize: 4,
                    columnClass: 'ml-3',
                    labelText: 'Required for Occupancy?',
                    componentProps: {
                      inputClassName: 'need-electrical-power-for-reoccupancy-select',
                    },
                    ComponentType: YesNoSelectMenu,
                    includeToolTip: false,
                  }}
                />
                <GridField
                  name={NEED_ELECTRICAL_POWER_FOR_FUNCTION}
                  form={form}
                  options={{
                    columnSize: 4,
                    columnClass: 'ml-3',
                    labelText: 'Required for Function?',
                    componentProps: {
                      inputClassName: 'need-electical-power-for-function-select',
                    },
                    ComponentType: YesNoSelectMenu,
                    includeToolTip: false,
                  }}
                />
              </FormGroup>
            </FormGroup>
            <FormGroup tag="fieldset">
              <legend>Elevator</legend>
              <GridField
                name={NEED_ELEVATOR_FOR_FUNCTION}
                form={form}
                options={{
                  columnSize: 4,
                  labelText: 'Need Elevator for Function?',
                  componentProps: {
                    inputClassName: 'need-elevator-for-function-select',
                  },
                  ComponentType: YesNoSelectMenu,
                  includeToolTip: true,
                }}
              />
              {showMaxWalkableStory && (
                <GridField
                  name={MAX_WALKABLE_STORY}
                  form={form}
                  options={{
                    columnSize: 4,
                    labelText: 'Max Walkable Story',
                    ComponentType: 'text',
                    includeToolTip: true,
                  }}
                />
              )}
              <GridField
                name={USE_UPDATED_ELEVATOR_COMPONENTS}
                form={form}
                options={{
                  columnSize: 4,
                  labelText: 'Use Updated Elevator Components?',
                  ComponentType: YesNoSelectMenu,
                  componentProps: {
                    includeEmptyOption: false,
                  },
                  includeToolTip: true,
                }}
              />
            </FormGroup>
            <FormGroup tag="fieldset">
              <legend>Temporary Repairs</legend>
              <GridField
                name={ALLOW_TEMPORARY_REPAIRS}
                form={form}
                options={{
                  columnSize: 4,
                  labelText: 'Allow Temporary Repairs to Regain Occupancy/Function?',
                  componentProps: {
                    inputClassName: 'allow-temporary-repairs-to-regain-function-select',
                  },
                  ComponentType: YesNoSelectMenu,
                  includeToolTip: true,
                }}
              />
              <GridField
                name={ALLOW_TEMPORARY_SHORING}
                form={form}
                options={{
                  columnSize: 4,
                  labelText: 'Allow Temporary Shoring to Regain Occupancy/Function?',
                  componentProps: {
                    inputClassName: 'allow-temporary-shoring-to-regain-function-select',
                  },
                  ComponentType: YesNoSelectMenu,
                  includeToolTip: true,
                }}
              />
            </FormGroup>
            <FormGroup tag="fieldset">
              <legend>Fire</legend>
              <GridField
                name={ALLOW_FIRE_WATCH_TO_BE_SUFFICIENT}
                form={form}
                options={{
                  columnSize: 4,
                  labelText: 'Allow Fire Watch to be Sufficient for Occupancy?',
                  componentProps: {
                    inputClassName: 'allow-fire-watch-to-regain-reoccupancy-select',
                  },
                  ComponentType: YesNoSelectMenu,
                  includeToolTip: true,
                }}
              />
            </FormGroup>
            <FormGroup tag="fieldset">
              <legend>Egress</legend>
              <GridField
                name={PERCENT_OF_EXITS_REQUIRED}
                form={form}
                options={{
                  columnSize: 4,
                  labelText: 'Percentage of the Exits Required for Occupancy?',
                  componentProps: {
                    inputClassName: 'percentage-of-exits-for-occupancy-text',
                  },
                  inputGroup: {
                    addonType: 'append',
                    addonText: '%',
                  },
                  ComponentType: 'text',
                  includeToolTip: true,
                }}
              />
              <GridField
                name={MINIMUM_NUMBER_OF_EXITS_REQUIRED}
                form={form}
                options={{
                  columnSize: 4,
                  labelText: 'Minimum Number of Exits Required for Occupancy?',
                  componentProps: {
                    inputClassName: 'minimum-number-of-exits-required-for-occupancy-text',
                  },
                  ComponentType: 'text',
                  includeToolTip: true,
                }}
              />
              <GridField
                name={DOOR_RACKING_AFFECTS_EGRESS}
                form={form}
                options={{
                  columnSize: 4,
                  labelText: 'Door Racking affects Egress?',
                  componentProps: {
                    inputClassName: 'door-racking-affects-egress-select',
                  },
                  ComponentType: YesNoSelectMenu,
                  includeToolTip: false,
                }}
              />
            </FormGroup>
            <FormGroup tag="fieldset">
              <legend>Smart Components</legend>
              <GridField
                name={USE_SMART_PIPING_COMPONENTS}
                form={form}
                options={{
                  columnSize: 4,
                  labelText: 'Use "Smart" Piping Components?',
                  componentProps: {
                    includeEmptyOption: false,
                    inputClassName: 'use-smart-piping-components-select',
                  },
                  ComponentType: YesNoSelectMenu,
                  includeToolTip: true,
                }}
              />
              <GridField
                name={USE_SMART_EQUIPMENT_COMPONENTS}
                form={form}
                options={{
                  columnSize: 4,
                  labelText: 'Use "Smart" Equipment Components?',
                  componentProps: {
                    includeEmptyOption: false,
                    inputClassName: 'use-smart-equipment-components-select',
                  },
                  ComponentType: YesNoSelectMenu,
                  includeToolTip: true,
                }}
              />
              {showEquipmentPrequalified && (
                <GridField
                  name={EQUIPMENT_PREQUALIFIED}
                  form={form}
                  options={{
                    columnSize: 4,
                    labelText: 'Is the Equipment Prequalified?',
                    componentProps: {
                      inputClassName: 'is-equipment-prequalified-select',
                    },
                    ComponentType: YesNoSelectMenu,
                    includeToolTip: true,
                  }}
                />
              )}
              {showPrequalifiedDesignCodeYear && (
                <GridField
                  name={PREQUALIFIED_DESIGN_CODE_YEAR}
                  form={form}
                  options={{
                    columnSize: 4,
                    labelText: 'For what Design Code was the Equipment Prequalified?',
                    componentProps: {
                      inputClassName: 'prequalified-design-code-year-select',
                    },
                    ComponentType: EquipmentPrequalifiedDesignCodeYearSelectMenu,
                    includeToolTip: true,
                  }}
                />
              )}
              {showPrequalifiedDetails && (
                <>
                  <GridField
                    name={PREQUALIFIED_ACC_GROUND}
                    form={form}
                    options={{
                      columnSize: 4,
                      labelText: 'Prequalification Acceleration at Ground Level',
                      componentProps: {
                        inputClassName: 'prequalified-acc-ground-text',
                      },
                      ComponentType: 'text',
                      includeToolTip: true,
                    }}
                  />
                  <GridField
                    name={PREQUALIFIED_ACC_ROOF}
                    form={form}
                    options={{
                      columnSize: 4,
                      labelText: 'Prequalification Acceleration at Roof Level',
                      componentProps: {
                        inputClassName: 'prequalified-acc-roof-text',
                      },
                      ComponentType: 'text',
                      includeToolTip: true,
                    }}
                  />
                </>
              )}
            </FormGroup>
            <FormGroup tag="fieldset">
              <legend>Flooding</legend>
              <GridField
                name={INCLUDE_FLOODING_IMPACT}
                form={form}
                options={{
                  columnSize: 4,
                  labelText: 'Include Flooding Consequences?',
                  componentProps: {
                    includeEmptyOption: false,
                    inputClassName: 'include-flooding-consequences-select',
                  },
                  ComponentType: YesNoSelectMenu,
                  includeToolTip: true,
                }}
              />
            </FormGroup>
            <FormGroup tag="fieldset">
              <legend>Surge Demand</legend>
              <GridField
                name={INCLUDE_SURGE_DEMAND}
                form={form}
                options={{
                  columnSize: 4,
                  labelText: 'Include Surge Demand?',
                  componentProps: {
                    inputClassName: 'include-surge-demand-select',
                  },
                  ComponentType: YesNoSelectMenu,
                  includeToolTip: true,
                }}
              />
              {showIsDenseUrbanArea && (
                <GridField
                  name={IS_DENSE_URBAN_AREA}
                  form={form}
                  options={{
                    columnSize: 4,
                    labelText: 'Is Dense Urban Area?',
                    componentProps: {
                      inputClassName: 'is-dense-urban-area-select',
                    },
                    ComponentType: YesNoSelectMenu,
                    includeToolTip: true,
                  }}
                />
              )}
            </FormGroup>
          </FormGroup>
        )}
        <BackButton onClick={onBack} />
        <NextButton onClick={onNext} />
      </Form>
    </div>
  );
};

export default BuildingStabilitySection;
